// Tasti della pagina ordine che vivono nelle tappe del percorso (redesign
// 2026-10-23). Stavano in ShippingSection e in testata: ognuno si porta dietro
// il suo busy e il suo errore, cosi' la tappa che lo mostra non deve sapere
// come funziona. Regola Davide: ogni tasto da' un esito, anche quando fallisce.
import { useCallback, useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { generateAllServiceLdvBulk } from "../../allserviceApi";
import {
  POLEEPO_BULK_STATUSES,
  downloadOrderBordero,
  downloadOrderLabel,
  setSupplierPaid,
} from "../../api";
import { STATO_SPEDITO } from "../../confermaBulk";
import type { Order } from "../../types";
import type { GenerateLabelClickOpts } from "../OrderCard";
import type { ShippingDraft } from "../ShippingEditor";
import { DisputeCausaDialog } from "./DisputeCausaDialog";
import { BTN, BTN_ICON } from "./stile";

export const EMPTY_DRAFT: ShippingDraft = {
  colli: 0,
  dirty: false,
  kg: 0,
  l: 0,
  a: 0,
  p: 0,
  pallet: false,
  dims: [],
  carrier: "",
};

/** Le opzioni di «Genera / Rifai LDV» dal draft dell'editor colli: WYSIWYG,
 *  senza dover premere «Salva» prima. Draft pulito → nessun override, i
 *  default li mette il backend. ``redo`` forza la rigenerazione anche senza
 *  modifiche (ordine 405-8958815-0420325, mittente sbagliato: dati giusti, LDV
 *  da rifare). */
export function opzioniLdv(draft: ShippingDraft, redo: boolean): GenerateLabelClickOpts {
  return {
    packagesCount: draft.colli || undefined,
    force: redo || draft.dirty,
    packageDimensions: draft.dirty
      ? draft.dims.length > 0
        ? draft.dims
        : [{ kg: draft.kg, l: draft.l, a: draft.a, p: draft.p }]
      : undefined,
    usePallet: draft.dirty ? draft.pallet : undefined,
    carrier: draft.carrier || undefined,
  };
}

export function haLdv(order: Order): boolean {
  return Boolean(order.trackingCode || order.ldvCode);
}

/** Tasto pieno della tappa: tutta la larghezza, testo che va a capo invece di
 *  uscire dalla colonna («Conferma a Maisons du Monde» non sta in 180px). */
export const BTN_TAPPA = `${BTN} w-full text-center leading-tight`;

function Icona({ d }: { d: string[] }) {
  return (
    <svg viewBox="0 0 24 24" className={BTN_ICON} aria-hidden="true">
      {d.map((p) => (
        <path key={p} d={p} />
      ))}
    </svg>
  );
}

function ConErrore({ errore, children }: { errore: string | null; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      {children}
      <ErrorBanner message={errore} />
    </div>
  );
}

/** «Scarica LDV»: il PDF sta sul server, ``GET /orders/{ref}/label`` lo serve.
 *  Prima si apriva solo nella scheda subito dopo la generazione — chiusa
 *  quella, l'etichetta non era piu' raggiungibile (Davide 2026-08-06). */
export function ScaricaLdvButton({ order }: { order: Order }) {
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  if (!haLdv(order)) return null;
  return (
    <ConErrore errore={errore}>
      <button
        type="button"
        data-testid="download-label-btn"
        title="Riapre il PDF della LDV già generata"
        disabled={busy}
        aria-busy={busy}
        onClick={() => {
          setBusy(true);
          setErrore(null);
          downloadOrderLabel(order.id)
            .catch((err: unknown) => setErrore(prettifyApiError(err)))
            .finally(() => setBusy(false));
        }}
        className={BTN_TAPPA}
      >
        <Icona d={["M12 3v12", "M7 12l5 5 5-5", "M5 21h14"]} />
        Scarica LDV
      </button>
    </ConErrore>
  );
}

/** Borderò BRT (D3): solo BRT — gli altri corrieri non ne hanno uno — e solo a
 *  LDV fatta: prima esce senza segnacolli, e NON è il documento del corriere. */
export function BorderoButton({ order }: { order: Order }) {
  const [errore, setErrore] = useState<string | null>(null);
  const brt = (order.corriere || "").toUpperCase().startsWith("BRT");
  if (!brt || !order.trackingCode) return null;
  return (
    <ConErrore errore={errore}>
      <button
        type="button"
        aria-label={`Borderò BRT ordine ${order.id}`}
        title="Scarica il borderò BRT da consegnare al corriere (PDF)"
        onClick={() => {
          setErrore(null);
          downloadOrderBordero(order.id).catch((err: unknown) => setErrore(prettifyApiError(err)));
        }}
        className={BTN_TAPPA}
      >
        <Icona
          d={[
            "M6 9V3h12v6",
            "M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2",
            "M6 14h12v7H6z",
          ]}
        />
        Borderò
      </button>
    </ConErrore>
  );
}

/** Contestazione SDA: il click apre la scelta della CAUSA e il download parte
 *  da li' (D6 «Tipo Pratica» e' la cella con cui SDA instrada il reclamo). */
export function ContestazioneButton({ order }: { order: Order }) {
  const [aperta, setAperta] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="dispute-open-btn"
        title="Scegli la causa e scarica l'XLSX di apertura pratica SDA da allegare al reclamo col corriere"
        onClick={() => setAperta(true)}
        className={BTN_TAPPA}
      >
        <Icona
          d={[
            "M14 3v4a1 1 0 0 0 1 1h4",
            "M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z",
            "M9 13h6M9 17h4",
          ]}
        />
        Contestazione
      </button>
      {aperta && <DisputeCausaDialog orderRef={order.id} onClose={() => setAperta(false)} />}
    </>
  );
}

/** «Segna pagato»: il campo e' ``paid`` (fattura fornitore), lo stesso del
 *  badge «fornitore da pagare». Niente ottimismo — sono soldi: si aspetta la
 *  risposta e si ricarica l'ordine. */
export function SegnaPagatoButton({ order, onChanged }: { order: Order; onChanged: () => void }) {
  const [pagato, setPagato] = useState(order.paid === true);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  useEffect(() => {
    setPagato(order.paid === true);
  }, [order.paid]);
  const segna = useCallback(async () => {
    setBusy(true);
    setErrore(null);
    try {
      const esito = await setSupplierPaid(order.id, !pagato);
      setPagato(esito.paid);
      onChanged();
    } catch (err) {
      setErrore(err instanceof Error ? err.message : "Non sono riuscito a salvare il pagamento");
    } finally {
      setBusy(false);
    }
  }, [order.id, pagato, onChanged]);
  return (
    <ConErrore errore={errore}>
      <button
        type="button"
        data-testid="supplier-paid-btn"
        disabled={busy}
        aria-busy={busy}
        aria-pressed={pagato}
        title={
          pagato
            ? "Il fornitore risulta pagato. Premi per togliere il segno: la data del pagamento viene cancellata."
            : "Segna che la fattura del fornitore e' stata pagata: spegne il badge «fornitore da pagare». Non tocca il marketplace."
        }
        onClick={() => void segna()}
        className={
          pagato
            ? `${BTN_TAPPA} !border-emerald-700 !bg-emerald-50 !text-emerald-800 hover:!bg-emerald-100`
            : BTN_TAPPA
        }
      >
        <svg viewBox="0 0 24 24" className={BTN_ICON} aria-hidden="true">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
          {pagato && <path d="M7 15l3 3 5-6" />}
        </svg>
        {busy ? "Segno…" : pagato ? "Fornitore pagato" : "Segna pagato"}
      </button>
    </ConErrore>
  );
}

/** LDV AllService di UN ordine: lo stesso xlsx del tasto unico della lista
 *  (``generateAllServiceLdvBulk``), qui con un ref solo. Prima dalla pagina
 *  ordine non si poteva: bisognava tornare in lista e selezionarlo. */
export function AllServiceLdvButton({
  order,
  onMessage,
  onChanged,
}: {
  order: Order;
  onMessage: (msg: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  return (
    <ConErrore errore={errore}>
      <button
        type="button"
        data-testid="allservice-ldv-btn"
        disabled={busy}
        aria-busy={busy}
        title="Genera il file LDV AllService (xlsx) di questo ordine"
        onClick={() => {
          setBusy(true);
          setErrore(null);
          generateAllServiceLdvBulk([order.id])
            .then((r) => {
              const primo = r.errors[0];
              if (primo) setErrore(primo.message);
              else onMessage(`LDV AllService generata: ${r.generated[0]?.file_name ?? order.id}.`);
              onChanged();
            })
            .catch((err: unknown) => setErrore(prettifyApiError(err)))
            .finally(() => setBusy(false));
        }}
        className={BTN_TAPPA}
      >
        {busy ? "Genero…" : "LDV AllService"}
      </button>
    </ConErrore>
  );
}

/** Lo stato Poleepo di questo ordine, a mano: la tendina della lista con un
 *  ordine solo. Il default è «Spedito» come in lista (Davide 2026-08-21), ma
 *  ogni scelta passa da una conferma: cambia l'ordine anche fuori da OM. */
export function CambiaStato({
  onApplica,
  busy,
}: {
  onApplica: (stato: string) => void;
  busy: boolean;
}) {
  const [stato, setStato] = useState(STATO_SPEDITO);
  return (
    <div className="flex gap-1.5">
      <select
        aria-label="Nuovo stato"
        value={stato}
        onChange={(e) => setStato(e.target.value)}
        className="min-h-[38px] min-w-0 flex-1 rounded-md border border-line-2 bg-card px-2 text-[13px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
      >
        {POLEEPO_BULK_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={busy}
        onClick={() => onApplica(stato)}
        className={`${BTN} shrink-0`}
      >
        {busy ? "Applico…" : "Applica"}
      </button>
    </div>
  );
}
